import React, { useState, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import suppliersService from '../services/suppliersService';

/**
 * SupplierSearch
 * Debounced (300ms) supplier autocomplete, a structural mirror of
 * ClientSearch.jsx over suppliersService. Searches name and RFC via the
 * backend's ?search= parameter and shows the top 10 matches.
 */
const SupplierSearch = ({ onSelect, selectedSupplier = null, disabled = false }) => {
  const { t } = useTranslation();
  // Stable id so the combobox input can point aria-controls at its listbox.
  const listboxId = useId();
  const [searchInput, setSearchInput] = useState(selectedSupplier?.name || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Keep the visible text in sync when the parent sets a supplier
  // programmatically (e.g. loading an existing order into the form).
  useEffect(() => {
    setSearchInput(selectedSupplier?.name || '');
  }, [selectedSupplier?.name]);

  const handleSearch = (query) => {
    setSearchInput(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearched(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsLoading(true);
        const response = await suppliersService.listSuppliers({ search: query });
        setSuggestions((response.data.results || []).slice(0, 10));
        setShowSuggestions(true);
        setSearched(true);
      } catch (err) {
        console.error('Error searching suppliers:', err);
        setSuggestions([]);
        setShowSuggestions(true);
        setSearched(true);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const handleSelectSupplier = (supplier) => {
    setSearchInput(supplier.name);
    setSuggestions([]);
    setShowSuggestions(false);
    onSelect(supplier);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutside =
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target);

      if (isClickOutside) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="entity-search-container">
      <input
        ref={inputRef}
        type="text"
        value={searchInput}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => searchInput.trim() && suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={t('purchases.searchSupplierPlaceholder')}
        disabled={disabled}
        className="form-input"
        role="combobox"
        aria-label={t('purchases.searchSupplierPlaceholder')}
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={listboxId}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          id={listboxId}
          className="entity-search-suggestions"
          role="listbox"
        >
          {suggestions.map((supplier) => (
            <div
              key={supplier.id}
              onClick={() => handleSelectSupplier(supplier)}
              className="entity-suggestion-item"
              role="option"
              aria-selected={selectedSupplier?.id === supplier.id}
            >
              <div className="entity-suggestion-name">{supplier.name}</div>
              <div className="entity-suggestion-meta">{supplier.rfc || '—'}</div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && searched && !isLoading && suggestions.length === 0 && (
        <div className="entity-search-empty" role="status">
          {t('purchases.noSuppliersFound')}
        </div>
      )}
    </div>
  );
};

export default SupplierSearch;
